import { getNotificationCategories } from '@frontend/api-client';
import type { NotificationSettingValue, NotificationType } from '@frontend/api-types';
import { isLast } from '@frontend/duck-tape';
import { Fragment } from '@frontend/react';
import { Divider, PageContainer, YStack } from '@frontend/web-react';
import { NotificationCategory } from '@frontend/web/components/DomainSpecific/Notifications/NotificationCategory';
import { useRetrieveNotificationSettingsQuery, useUpdateNotificationSettingsMutation } from '@frontend/web/hooks';
import { createFileRoute } from '@tanstack/react-router';

const Notifications = () => {
  const { data: notificationSettings, isLoading } = useRetrieveNotificationSettingsQuery();
  const [updateNotificationSettings] = useUpdateNotificationSettingsMutation();
  const categories = getNotificationCategories('web');

  const handleToggle = (category: string, type: NotificationType) => {
    if (notificationSettings) {
      const value = notificationSettings[category]! as NotificationSettingValue;

      const newValue =
        ['sms', 'email', 'push']
          .filter(
            (option) => (value.includes(option) && option !== type) || (!value.includes(option) && option === type),
          )
          .join('_') || 'none';

      updateNotificationSettings({
        [category]: newValue,
      });
    }
  };

  return (
    <PageContainer className="bg-neutral-50" headerTitle="Preferences" isLoading={isLoading || !notificationSettings}>
      {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
      <YStack className="xl:max-w-[1000px]" gapY="lg">
        {notificationSettings
          ? categories.map(({ category, featureFlag, ...restOfProps }, index, array) => (
              <Fragment key={category}>
                <NotificationCategory
                  category={category}
                  isTitleSwitchActive={notificationSettings[featureFlag as string] as boolean}
                  onToggle={handleToggle}
                  onToggleTitleSwitch={
                    featureFlag
                      ? () =>
                          updateNotificationSettings({
                            [featureFlag as string]: !notificationSettings[featureFlag as string],
                          })
                      : undefined
                  }
                  value={notificationSettings[category]! as NotificationSettingValue}
                  {...restOfProps}
                />
                {isLast(array, index) ? null : <Divider />}
              </Fragment>
            ))
          : null}
      </YStack>
    </PageContainer>
  );
};

export const Route = createFileRoute('/app/settings/notifications')({
  component: Notifications,
});
